package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList

// Gentle "fade up" as content scrolls into view, on the redesigned site pages (never on the template previews).
// Below the hero, each section's blocks are revealed once; grids of 3+ cards come in one after another.
// Only things below the first screen are hidden, so nothing flickers on load. Without JavaScript, or with
// "reduce motion" switched on, everything simply shows. All changes are inline styles that are removed once
// the animation ends, so sticky and fixed elements behave normally afterwards.

private const val SHIFT = "translateY(18px)"
private const val DURATION_MS = 650
private const val STAGGER_MS = 80
private const val MAX_STAGGER_STEPS = 5
private const val MIN_GRID_ITEMS = 3

/** Put once at the end of a redesigned page. */
@Composable
fun RdScrollReveal() {
    DisposableEffect(Unit) {
        val observer = if (prefersReducedMotion()) null else startReveal()
        onDispose { observer?.disconnect() }
    }
}

private fun prefersReducedMotion() = window.matchMedia("(prefers-reduced-motion: reduce)").matches

private fun startReveal(): dynamic {
    val pending = mutableMapOf<Element, Int>() // element -> stagger delay

    var observer: dynamic = null
    val onIntersect: (dynamic) -> Unit = { entries ->
        for (entry in (entries as Array<dynamic>)) {
            if (!(entry.isIntersecting as Boolean)) continue
            val element = entry.target as HTMLElement
            observer.unobserve(element)
            reveal(element, pending.remove(element) ?: 0)
        }
    }
    // Kotlin's DOM bindings have no IntersectionObserver, so it is created through JS.
    observer = js("(function(cb){ return new IntersectionObserver(cb, { rootMargin: '0px 0px -8% 0px' }); })")(onIntersect)

    // Wait one frame so the page has been laid out before measuring.
    window.requestAnimationFrame {
        revealUnits().forEach { (element, delay) ->
            if (element.getBoundingClientRect().top < window.innerHeight) return@forEach // already on screen
            hide(element)
            pending[element] = delay
            observer.observe(element)
        }
    }
    return observer
}

/** The blocks to animate: every section after the hero, block by block; card grids item by item. */
private fun revealUnits(): List<Pair<HTMLElement, Int>> {
    val sections = document.querySelectorAll("main section").asList().filterIsInstance<HTMLElement>()
    val units = mutableListOf<Pair<HTMLElement, Int>>()
    sections.drop(1).forEach { section ->
        if (sections.any { it !== section && it.contains(section) }) return@forEach // nested section
        val wrap = section.firstElementChild as? HTMLElement ?: return@forEach
        val blocks = if (wrap.children.length > 0 && wrap.tagName == "DIV") wrap.children.asList() else section.children.asList()
        blocks.filterIsInstance<HTMLElement>().forEach { block ->
            if (isCardGrid(block)) {
                block.children.asList().filterIsInstance<HTMLElement>().forEachIndexed { index, card ->
                    units += card to (index % (MAX_STAGGER_STEPS + 1)) * STAGGER_MS
                }
            } else {
                units += block to 0
            }
        }
    }
    return units.filter { (element, _) -> canAnimate(element) }
}

private fun isCardGrid(element: HTMLElement): Boolean {
    if (element.children.length < MIN_GRID_ITEMS || element.tagName == "FORM") return false
    val display = window.getComputedStyle(element).display
    return display == "grid" || display == "flex"
}

/** Leave alone anything that already uses inline transform/opacity, or that is fixed/sticky. */
private fun canAnimate(element: HTMLElement): Boolean {
    val inline = element.style
    if (inline.getPropertyValue("transform").isNotEmpty() || inline.getPropertyValue("opacity").isNotEmpty()) return false
    val position = window.getComputedStyle(element).position
    return position != "fixed" && position != "sticky"
}

private fun hide(element: HTMLElement) {
    element.style.setProperty("opacity", "0")
    element.style.setProperty("transform", SHIFT)
}

private fun reveal(element: HTMLElement, delayMs: Int) {
    element.style.setProperty(
        "transition",
        "opacity ${DURATION_MS}ms ease ${delayMs}ms, transform ${DURATION_MS}ms cubic-bezier(.2,.7,.2,1) ${delayMs}ms"
    )
    element.style.setProperty("opacity", "1")
    element.style.setProperty("transform", "none")
    window.setTimeout({
        element.style.removeProperty("transition")
        element.style.removeProperty("opacity")
        element.style.removeProperty("transform")
    }, DURATION_MS + delayMs + 50)
}
